'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'

import { useSession } from 'next-auth/react'

import { CookieNamesEnum } from '@/enums/CookieNamesEnum'
import { getRequestSiteContext } from '@/helpers/getRequestSiteContext'
import { useCookie } from '@/hooks/useCookie'
import { useTenant } from '@/hooks/useTenant'
import { defaultSiteContextMode } from '@/lib/config.shared'
import { getTenantMarkets } from '@/lib/tenant/getTenantMarkets'
import { getTenantDefaultSiteContext, resolveSiteContext } from '@/lib/tenant/resolveSiteContext'

import type { SiteContextModeEnum } from '@/enums/SiteContextModeEnum'

const ServerCookiesContext = React.createContext<string | undefined>(undefined)

export const useServerCookies = () => React.useContext(ServerCookiesContext)

export const SiteContext = React.createContext<SiteContextProps>({
  siteContext: defaultSiteContextMode,
  setSiteContext: () => null,
  isSet: false,
})

interface SiteContextProps {
  siteContext: SiteContextModeEnum
  setSiteContext: (value: SiteContextModeEnum) => void
  isSet: boolean
}

interface SiteContextProviderProps {
  serverCookies?: string
  children: React.ReactNode
}

const useRefreshServerContentOnSiteChange = (activeSite: SiteContextModeEnum) => {
  const router = useRouter()
  const renderedSite = React.useRef(activeSite)

  React.useEffect(() => {
    if (renderedSite.current === activeSite) {
      return
    }

    renderedSite.current = activeSite
    router.refresh()
  }, [activeSite, router])
}

const SiteContextProvider: React.FC<SiteContextProviderProps> = React.memo(({ children, serverCookies }) => {
  const tenant = useTenant()
  const { data: session } = useSession()

  const validSites = React.useMemo(() => getTenantMarkets(tenant.id), [tenant.id])

  const tenantDefaultSite = React.useMemo(() => getTenantDefaultSiteContext(tenant), [tenant])

  const [siteContext, setSiteContext, isSet] = useCookie(CookieNamesEnum.Site, tenantDefaultSite, serverCookies)

  useRefreshServerContentOnSiteChange(resolveSiteContext(tenant, siteContext))

  React.useEffect(() => {
    const expectedSite = getRequestSiteContext(tenant, session, undefined, siteContext)

    if (siteContext !== expectedSite || (!isSet && validSites.length === 1)) {
      setSiteContext(expectedSite)
    }
  }, [siteContext, setSiteContext, validSites, tenant, session, isSet])

  const contextValue = React.useMemo(
    () => ({ siteContext, setSiteContext, isSet }),
    [siteContext, setSiteContext, isSet]
  )

  return (
    <ServerCookiesContext.Provider value={serverCookies}>
      <SiteContext.Provider value={contextValue}>{children}</SiteContext.Provider>
    </ServerCookiesContext.Provider>
  )
})

SiteContextProvider.displayName = 'SiteContextProvider'

export default SiteContextProvider
